import { useEffect } from 'react';

/**
 * Full-screen preview for a Folder file.
 *
 * WHY NOT PdfViewerOverlay: that one fetches BYTES and renders them with
 * pdf.js, which works because CG Sign serves them from our own origin. A
 * Folder file lives in Blob Storage and is reached by a short-lived SAS
 * URL, so a `fetch()` from the browser is a cross-origin request and dies
 * on CORS unless the storage account is configured for it. An <iframe>
 * and an <img> are plain navigations - no CORS, no proxying, and the
 * bytes still never pass through the API.
 *
 * This works because vault uploads are stored with
 * `Content-Disposition: inline` (blob.ts default). Were they `attachment`,
 * the iframe would download the file instead of showing it.
 *
 * NOT EVERYTHING PREVIEWS. A browser renders PDFs, images and plain text;
 * it cannot render .xlsx, .docx or a zip. Those get an honest empty state
 * with the download button, rather than an iframe showing nothing.
 */

/** Can the browser display this in place? */
export function canPreview(contentType: string): boolean {
  return (
    contentType === 'application/pdf' ||
    contentType.startsWith('image/') ||
    contentType === 'text/plain' ||
    contentType === 'text/csv'
  );
}

export function FilePreviewOverlay({
  fileName,
  subtitle,
  contentType,
  url,
  loading,
  error,
  onDownload,
  onClose,
}: {
  fileName: string;
  subtitle?: string;
  contentType: string;
  /** The SAS URL. null while it is still being fetched. */
  url: string | null;
  loading: boolean;
  error: string | null;
  onDownload: () => void;
  onClose: () => void;
}) {
  // Escape closes, and the page behind must not scroll under the overlay.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  const previewable = canPreview(contentType);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-slate-100"
      role="dialog"
      aria-modal="true"
      aria-label={`Preview of ${fileName}`}
    >
      <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-5 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-slate-900">{fileName}</p>
          {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onDownload}
            className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700"
          >
            Download
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {loading ? (
          <p className="pt-16 text-center text-sm text-slate-500">Opening...</p>
        ) : error ? (
          <p role="alert" className="pt-16 text-center text-sm text-red-700">{error}</p>
        ) : !previewable || !url ? (
          <div className="mx-auto max-w-md pt-16 text-center">
            <p className="text-sm font-medium text-slate-700">
              This kind of file cannot be shown in the browser.
            </p>
            <p className="mt-1 text-sm text-slate-500">
              Download it to open in the app it belongs to.
            </p>
            <button
              type="button"
              onClick={onDownload}
              className="mt-4 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700"
            >
              Download {fileName}
            </button>
          </div>
        ) : contentType.startsWith('image/') ? (
          <img
            src={url}
            alt={fileName}
            className="mx-auto max-h-full rounded-lg bg-white shadow-sm"
          />
        ) : (
          /* PDFs and plain text: the browser's own viewer. */
          <iframe
            src={url}
            title={fileName}
            className="mx-auto h-full w-full max-w-5xl rounded-lg border border-slate-200 bg-white"
          />
        )}
      </div>
    </div>
  );
}
